import axios from 'axios';

const baseUrl = process.env.REACT_APP_API_BASE_URL;

const api = axios.create({
    baseURL: baseUrl,
    headers: {
        'Content-Type': 'application/json',
    },
});

api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('fms-maintenance-token');
        if (token) {
            config.headers.authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

api.interceptors.response.use(
    (response) => {
        return response;
    },
    (error) => {
        if (error.response && error.response.status === 401) {
            localStorage.removeItem('fms-maintenance-token');
            localStorage.removeItem('fms-maintenance-details');

            window.location.href = '/';
        }
        return Promise.reject(error);
    }
);

export const apiService = {
    login: async (credentials) => {
        try {
            const response = await api.post('/auth/login', credentials);
            return response.data;
        } catch (error) {
            throw error?.response?.data?.message[0];
        }
    },

    register: async (credentials) => {
        try {
            const response = await api.post('/auth/sign-up', credentials);
            return response.data;
        } catch (error) {
            throw error?.response?.data?.message[0];
        }
    },

    uploadCsv: async (formData) => {
        try {
            const response = await api.post('/imports/csv', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            return response.data;
        } catch (error) {
            throw error?.response?.data?.message[0];
        }
    },

    proceedCsv: async (formData) => {
        try {
            const response = await api.post('/imports/csv-proceed', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            return response.data;
        } catch (error) {
            throw error?.response?.data?.message[0];
        }
    },

    getMaintenanceData: async (params) => {
        try {
            const response = await api.post(`/maintenance/paginated?page=${params.page}&limit=${params.limit}`);
            return response.data;
        } catch (error) {
            throw error?.response?.data?.message[0];
        }
    },

    getTyreData: async (params) => {
        try {
            const response = await api.post(`/maintenance/paginated-tyre?page=${params.page}&limit=${params.limit}`);
            return response.data;
        } catch (error) {
            throw error?.response?.data?.message[0];
        }
    },

    getUsersRole: async () => {
        try {
            const response = await api.get(`/auth/user-roles-dropdown`);
            return response.data;
        } catch (error) {
            throw error?.response?.data?.message[0];
        }
    },

    createUser: async (userData) => {
        try {
            const response = await api.post('/auth/create-user', userData);
            return response.data;
        } catch (error) {
            throw error?.response?.data?.message[0];
        }
    },

    deleteUser: async (id) => {
        try {
            const response = await api.delete(`/auth/user-delete/${id}`);
            return response.data;
        } catch (error) {
            throw error?.response?.data?.message[0];
        }
    },

    deletePermission: async (id) => {
        try {
            const response = await api.delete(`/app-permissions/permission-group/${id}`);
            return response.data;
        } catch (error) {
            throw error?.response?.data?.message[0];
        }
    },

    deleteService: async (id) => {
        try {
            const response = await api.delete(`/maintenance-service/delete/${id}`);
            return response.data;
        } catch (error) {
            throw error?.response?.data?.message[0];
        }
    },

    getSingleService: async (id) => {
        try {
            const response = await api.get(`/maintenance-service/single/${id}`);
            return response.data;
        } catch (error) {
            throw error?.response?.data?.message[0];
        }
    },

    getServiceOverview: async () => {
        try {
            const response = await api.get(`/maintenance-service/overview`);
            return response.data;
        } catch (error) {
            throw error?.response?.data?.message[0];
        }
    },

    getSingleUser: async (id) => {
        try {
            const response = await api.get(`/auth/user-singe/${id}`);
            return response.data;
        } catch (error) {
            throw error?.response?.data?.message[0];
        }
    },

    getUserDropdown: async (id) => {
        try {
            const response = await api.get(`/auth/users-dropdown`);
            return response.data;
        } catch (error) {
            throw error?.response?.data?.message[0];
        }
    },

    editUser: async (userData) => {
        try {
            const response = await api.post('/auth/edit-user', userData);
            return response.data;
        } catch (error) {
            throw error?.response?.data?.message[0];
        }
    },

    getPaginatedUsers: async (params) => {
        try {
            const url = `/auth/users-paginated?page=${params.page}&limit=${params.limit}`;

            const body = params.search_text ? { search_text: params.search_text } : {};
            if (params.searchFilters) {
                Object.assign(body, params.searchFilters);
            }
            const response = await api.post(url, body);
            return response.data;
        } catch (error) {
            throw error?.response?.data?.message[0];
        }
    },

    getPermissionApi: async (params) => {
        try {
            const url = `/auth/permission-paginated?page=${params.page}&limit=${params.limit}`;

            const body = params.search_text ? { search_text: params.search_text } : {};
            if (params.searchFilters) {
                Object.assign(body, params.searchFilters);
            }

            const response = await api.post(url, body);
            return response.data;
        } catch (error) {
            throw error?.response?.data?.message[0];
        }
    },

    getPermissionDropdown: async () => {
        try {
            const response = await api.get(`/auth/permission-dropdown`);
            return response.data;
        } catch (error) {
            throw error?.response?.data?.message[0];
        }
    },

    getAllPermissionsDropdown: async () => {
        try {
            const response = await api.get(`/app-permissions/permissions-dropdown-badges`);
            return response.data;
        } catch (error) {
            throw error?.response?.data?.message[0];
        }
    },

    getFetchModules: async () => {
        try {
            const response = await api.get(`/auth/fetch-modules`);
            return response.data;
        } catch (error) {
            throw error?.response?.data?.message[0];
        }
    },

    createPermissionGroup: async (permissionData) => {
        try {
            const response = await api.post('/auth/create-permission-group', permissionData);
            return response.data;
        } catch (error) {
            throw error?.response?.data?.message[0];
        }
    },

    getSinglePermissionGroup: async (id) => {
        try {
            const response = await api.get(`/app-permissions/single-permission-group/${id}`);
            return response.data;
        } catch (error) {
            throw error?.response?.data?.message[0];
        }
    },

    updatePermissionGroup: async (permissionData) => {
        try {
            const response = await api.patch('/auth/update-permission-group', permissionData);
            return response.data;
        } catch (error) {
            throw error?.response?.data?.message[0];
        }
    },

    forgotPassword: async (email) => {
        try {
            const response = await api.post('/auth/recovery-password', {
                email: email
            });
            return response.data;
        } catch (error) {
            throw error?.response?.data?.message[0];
        }
    },

    resetPassword: async (passwordData) => {
        try {
            const response = await api.post('/auth/set-new-password', {
                password: passwordData.password,
                confirm_password: passwordData.confirm_password,
                token: passwordData.token
            });
            return response.data;
        } catch (error) {
            throw error?.response?.data?.message[0];
        }
    },

    changePassword: async (passwordData) => {
        try {
            const response = await api.post('/auth/change-password', {
                password: passwordData.password,
                confirm_password: passwordData.confirm_password,
            });
            return response.data;
        } catch (error) {
            throw error?.response?.data?.message[0];
        }
    },

    resetDefaultPassword: async (userId) => {
        try {
            const response = await api.post('/auth/default-password', {
                user_id: userId
            });
            return response.data;
        } catch (error) {
            throw error?.response?.data?.message[0];
        }
    },

    getVehiclesDropdown: async () => {
        try {
            const response = await api.get(`/vehicles/dropdown`);
            return response.data;
        } catch (error) {
            throw error?.response?.data?.message[0];
        }
    },

    getVehicleDetail: async (vehicleId, vehicleBrandId, ref_id) => {
        try {
            const response = await api.get(`/vehicles/detail?vehicle_id=${vehicleId}&vehicle_brand_id=${vehicleBrandId}&ref_id=${ref_id}`);
            return response.data;
        } catch (error) {
            throw error?.response?.data?.message[0];
        }
    },

    createServiceIssue: async (issueData) => {
        try {
            const response = await api.post('/service-issue/create', issueData);
            return response.data;
        } catch (error) {
            throw error?.response?.data?.message[0];
        }
    },

    updateServiceIssue: async (issueData) => {
        try {
            const response = await api.patch('/service-issue/update', issueData);
            return response.data;
        } catch (error) {
            throw error?.response?.data?.message[0];
        }
    },

    createServiceEntry: async (serviceData) => {
        try {
            const response = await api.post('/maintenance-service/create', serviceData);
            return response.data;
        } catch (error) {
            throw error?.response?.data?.message[0];
        }
    },

    updateServiceEntry: async (serviceData) => {
        try {
            const response = await api.patch('/maintenance-service/patch', serviceData);
            return response.data;
        } catch (error) {
            throw error?.response?.data?.message[0];
        }
    },

    uploadFiles: async (formData) => {
        try {
            const response = await api.post('/files-upload', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            return response.data;
        } catch (error) {
            throw error?.response?.data?.message[0];
        }
    },

    getReasonsDropdown: async () => {
        try {
            const response = await api.get(`/maintenance/reasons-dropdown`);
            return response.data;
        } catch (error) {
            throw error?.response?.data?.message[0];
        }
    },

    getCategoriesDropdown: async () => {
        try {
            const response = await api.get(`/maintenance/categories-dropdown`);
            return response.data;
        } catch (error) {
            throw error?.response?.data?.message[0];
        }
    },

    getPartsList: async (vehicleTypeId, vehicleId) => {
        try {
            const response = await api.get(`/maintenance/parts-list?vehicle_type_id=${vehicleTypeId}&vehicle_id=${vehicleId}`);
            return response.data;
        } catch (error) {
            throw error?.response?.data?.message[0];
        }
    },

    createServiceLineItem: async (lineItemData) => {
        try {
            const response = await api.post('/service-line-items/create', lineItemData);
            return response.data;
        } catch (error) {
            throw error?.response?.data?.message[0];
        }
    },

    updateServiceLineItem: async (lineItemData) => {
        try {
            const response = await api.patch('/service-line-items/update', lineItemData);
            return response.data;
        } catch (error) {
            throw error?.response?.data?.message[0];
        }
    },

    getServicesPaginated: async (params) => {
        try {
            const url = `/maintenance-service/paginated?page=${params.page}&limit=${params.limit}`;

            const body = params.search_text ? { search_text: params.search_text } : {};
            if (params.searchFilters) {
                Object.assign(body, params.searchFilters);
            }

            const response = await api.post(url, body);
            return response.data;
        } catch (error) {
            throw error?.response?.data?.message[0];
        }
    },

    getServicesReminderPaginated: async (params) => {
        try {
            const url = `/maintenance-service/upcoming-paginated?page=${params.page}&limit=${params.limit}&vehicle_id=${params.vehicle_id}`;
            const response = await api.post(url);
            return response.data;
        } catch (error) {
            throw error?.response?.data?.message[0];
        }
    },

    servicePdfExport: async (getIds) => {
        try {
            const response = await api.post('/reports/service-pdf', getIds);
            return response.data;
        } catch (error) {
            throw error?.response?.data?.message[0];
        }
    },

    serviceExcelExport: async (getIds) => {
        try {
            const response = await api.post('/reports/service-excel', getIds);
            return response.data;
        } catch (error) {
            throw error?.response?.data?.message[0];
        }
    },

    deleteServiceLineItem: async (id) => {
        try {
            const response = await api.delete(`/service-line-items/delete/${id}`);
            return response.data;
        } catch (error) {
            throw error?.response?.data?.message[0];
        }
    },

    updateServiceStatus: async (statusData) => {
        try {
            const response = await api.patch('/maintenance-service/change-status', statusData);
            return response.data;
        } catch (error) {
            throw error?.response?.data?.message[0];
        }
    },


    // ============== ISSUES ===================


    getIssuesOverview: async () => {
        try {
            const response = await api.get(`/service-issue/overview`);
            return response.data;
        } catch (error) {
            throw error?.response?.data?.message[0];
        }
    },

    getIssuesPaginated: async (params) => {
        try {
            const url = `/service-issue/paginated?page=${params.page}&limit=${params.limit}`;

            const body = params.search_text ? { search_text: params.search_text } : {};
            if (params.searchFilters) {
                Object.assign(body, params.searchFilters);
            }

            const response = await api.post(url, body);
            return response.data;
        } catch (error) {
            throw error?.response?.data?.message[0];
        }
    },

    getUserIssuesPaginated: async (params) => {
        try {
            const url = `/service-issue/view-all-paginated?assign_to=${params.userId}&page=${params.page}&limit=${params.limit}`;
            const response = await api.get(url);
            return response.data;
        } catch (error) {
            throw error?.response?.data?.message[0];
        }
    },

    deleteIssue: async (id) => {
        try {
            const response = await api.delete(`/service-issue/delete/${id}`);
            return response.data;
        } catch (error) {
            throw error?.response?.data?.message[0];
        }
    },

    getIssueById: async (id) => {
        try {
            const response = await api.get(`/service-issue/detail/${id}`);
            return response.data;
        } catch (error) {
            throw error?.response?.data?.message[0];
        }
    },

    issuePdfExport: async (getIds) => {
        try {
            const response = await api.post('/reports/issue-service-pdf', getIds);
            return response.data;
        } catch (error) {
            throw error?.response?.data?.message[0];
        }
    },

    issueExcelExport: async (getIds) => {
        try {
            const response = await api.post('/reports/issue-excel', getIds);
            return response.data;
        } catch (error) {
            throw error?.response?.data?.message[0];
        }
    },

    issueLastId: async () => {
        try {
            const response = await api.get(`/service-issue/last-id`);
            return response.data;
        } catch (error) {
            throw error?.response?.data?.message[0];
        }
    },

    getServiceEntryDropdown: async () => {
        try {
            const response = await api.get(`/maintenance-service/dropdown`);
            return response.data;
        } catch (error) {
            throw error?.response?.data?.message[0];
        }
    },

    getServiceEntryByVehicleDropdown: async (vehicle_id) => {
        try {
            const response = await api.get(`/maintenance-service/dropdown?vehicle_id=${vehicle_id}`);
            return response.data;
        } catch (error) {
            throw error?.response?.data?.message[0];
        }
    },

    createLinkIssue: async (linkIssueData) => {
        try {
            const response = await api.patch('/service-issue/link-issue', linkIssueData);
            return response.data;
        } catch (error) {
            throw error?.response?.data?.message[0];
        }
    },

    updatePriority: async (priorityData) => {
        try {
            const response = await api.patch('/service-issue/change-priority', priorityData);
            return response.data;
        } catch (error) {
            throw error?.response?.data?.message[0];
        }
    },

    updateServicePriority: async (priorityData) => {
        try {
            const response = await api.patch('/maintenance-service/change-priority', priorityData);
            return response.data;
        } catch (error) {
            throw error?.response?.data?.message[0];
        }
    },

    updateStatus: async (statusData) => {
        try {
            const response = await api.patch('/service-issue/change-status', statusData);
            return response.data;
        } catch (error) {
            throw error?.response?.data?.message[0];
        }
    },

    updateTags: async (tagsData) => {
        try {
            const response = await api.post('/service-issue/add-tags', tagsData);
            return response.data;
        } catch (error) {
            throw error?.response?.data?.message[0];
        }
    },

    updateAssignee: async (assigneeData) => {
        try {
            const response = await api.patch('/service-issue/change-assignee', assigneeData);
            return response.data;
        } catch (error) {
            throw error?.response?.data?.message[0];
        }
    },

    getIssueHistory: async (params) => {
        try {
            const url = `/service-issue/issue-history?history_type=${params.history_type}&service_issue_id=${params.service_issue_id}`;

            const response = await api.get(url);
            return response.data;
        } catch (error) {
            throw error?.response?.data?.message[0];
        }
    },

    commentOnIssue: async (data) => {
        try {
            const response = await api.post('/service-issue/comment-on-issue', data);
            return response.data;
        } catch (error) {
            throw error?.response?.data?.message[0];
        }
    },


    // ============== WORK ORDER ===================


    getWorkOrderOverview: async () => {
        try {
            const response = await api.get(`/work-orders/overview`);
            return response.data;
        } catch (error) {
            throw error?.response?.data?.message[0];
        }
    },

    getWorkOrderPaginated: async (params) => {
        try {
            const url = `/work-orders/paginated?page=${params.page}&limit=${params.limit}`;

            const body = params.search_text ? { search_text: params.search_text } : {};
            if (params.searchFilters) {
                Object.assign(body, params.searchFilters);
            }

            const response = await api.post(url, body);
            return response.data;
        } catch (error) {
            throw error?.response?.data?.message[0];
        }
    },

    getVendorsDropdown: async () => {
        try {
            const response = await api.get(`/maintenance/vendors-dropdown`);
            return response.data;
        } catch (error) {
            throw error?.response?.data?.message[0];
        }
    },

    createWorkOrder: async (data) => {
        try {
            const response = await api.post('/work-orders/create', data);
            return response.data;
        } catch (error) {
            throw error?.response?.data?.message[0];
        }
    },

    workOrderPdfExport: async (getIds) => {
        try {
            const response = await api.post('/reports/work-order-pdf', getIds);
            return response.data;
        } catch (error) {
            throw error?.response?.data?.message[0];
        }
    },

    workOrderExcelExport: async (getIds) => {
        try {
            const response = await api.post('/reports/work-order-excel', getIds);
            return response.data;
        } catch (error) {
            throw error?.response?.data?.message[0];
        }
    },

    deleteWorkOrder: async (id) => {
        try {
            const response = await api.delete(`/work-orders/delete/${id}`);
            return response.data;
        } catch (error) {
            throw error?.response?.data?.message[0];
        }
    },

    getWorkOrderHistory: async (params) => {
        try {
            const url = `/work-orders/work-order-history?history_type=${params.history_type}&work_order_id=${params.work_order_id}`;

            const response = await api.get(url);
            return response.data;
        } catch (error) {
            throw error?.response?.data?.message[0];
        }
    },

    commentOnWorkOrder: async (data) => {
        try {
            const response = await api.post('/work-orders/comment-on-work-order', data);
            return response.data;
        } catch (error) {
            throw error?.response?.data?.message[0];
        }
    },

    getWorkOrderById: async (id) => {
        try {
            const response = await api.get(`/work-orders/detail/${id}`);
            return response.data;
        } catch (error) {
            throw error?.response?.data?.message[0];
        }
    },

    updateWorkOrder: async (workOrderData) => {
        try {
            const response = await api.patch('/work-orders/update', workOrderData);
            return response.data;
        } catch (error) {
            throw error?.response?.data?.message[0];
        }
    },

    updateRepairPriority: async (priorityData) => {
        try {
            const response = await api.patch('/work-orders/update-repair-class', priorityData);
            return response.data;
        } catch (error) {
            throw error?.response?.data?.message[0];
        }
    },

    updateVendor: async (vendorData) => {
        try {
            const response = await api.patch('/work-orders/update-vendor', vendorData);
            return response.data;
        } catch (error) {
            throw error?.response?.data?.message[0];
        }
    },

    workOrderLinkServiceEntry: async (linkServiceData) => {
        try {
            const response = await api.patch('/work-orders/link-services', linkServiceData);
            return response.data;
        } catch (error) {
            throw error?.response?.data?.message[0];
        }
    },

    emailReminder: async (emailReminderData) => {
        try {
            const response = await api.post('/work-orders/email-reminder', emailReminderData);
            return response.data;
        } catch (error) {
            throw error?.response?.data?.message[0];
        }
    },

    financialApprovalStatus: async (financialApprovalData) => {
        try {
            const response = await api.patch('/work-orders/change-approval-financial-status', financialApprovalData);
            return response.data;
        } catch (error) {
            throw error?.response?.data?.message[0];
        }
    },

    operationalApprovalStatus: async (operationApprovalData) => {
        try {
            const response = await api.patch('/work-orders/change-approval-operation-status', operationApprovalData);
            return response.data;
        } catch (error) {
            throw error?.response?.data?.message[0];
        }
    },

    updateWorkOrderAssignee: async (assigneeData) => {
        try {
            const response = await api.patch('/work-orders/change-assignee', assigneeData);
            return response.data;
        } catch (error) {
            throw error?.response?.data?.message[0];
        }
    },

    // ============== TAGS ===================

    getServiceTagsDropdown: async () => {
        try {
            const response = await api.get(`/maintenance-service/tags-dropdown`);
            return response.data;
        } catch (error) {
            throw error?.response?.data?.message[0];
        }
    },

    getIssueTagsDropdown: async () => {
        try {
            const response = await api.get(`/service-issue/tags-dropdown`);
            return response.data;
        } catch (error) {
            throw error?.response?.data?.message[0];
        }
    },

    getWorkOrderTagsDropdown: async () => {
        try {
            const response = await api.get(`/work-orders/tags-dropdown`);
            return response.data;
        } catch (error) {
            throw error?.response?.data?.message[0];
        }
    },

    updateWorkOrderStatus: async (statusData) => {
        try {
            const response = await api.patch('/work-orders/change-status', statusData);
            return response.data;
        } catch (error) {
            throw error?.response?.data?.message[0];
        }
    },


    // ============== NOTIFICATIONS ===================

    getNotifications: async () => {
        try {
            const response = await api.get('/notifications/paginated');
            return response.data;
        } catch (error) {
            throw error?.response?.data?.message[0];
        }
    },

    seenAllNotifications: async () => {
        try {
            const response = await api.post('/notifications/seen-all', {});
            return response.data;
        } catch (error) {
            throw error?.response?.data?.message[0];
        }
    }
};